import Keycloak, {
  type KeycloakInitOptions as NativeKeycloakInitOptions,
  type KeycloakLoginOptions as NativeKeycloakLoginOptions,
  type KeycloakProfile as NativeKeycloakProfile,
} from "keycloak-js";
import {
  clearAuthContext,
  getAccessToken,
  setAccessToken,
} from "./auth-context";
import { isAppRuntime } from "./device";
import {
  normalizeLegacyHash,
  stripAuthResponseFragment,
} from "@wkt/router/moduleRegistry";

interface KeycloakInitOptions {
  checkLoginIframe?: boolean;
  onLoad?: "check-sso" | "login-required";
  responseMode?: "query" | "fragment";
  silentCheckSsoFallback?: boolean;
  silentCheckSsoRedirectUri?: string;
}

type KeycloakProfile = NativeKeycloakProfile & {
  id?: string;
  [key: string]: unknown;
};

type KeycloakLoginOptions = NativeKeycloakLoginOptions;
type KeycloakInstanceLike = Keycloak;

const CORDOVA_INIT_WAIT_INTERVAL_MS = 100;
const CORDOVA_INIT_MAX_ATTEMPTS = 150;

let refreshIntervalId: number | null = null;
let initPromise: Promise<boolean> | null = null;
let runtimeProfile: KeycloakProfile | null = null;
let runtimeInstance: KeycloakInstanceLike | null = null;

function canUseWindow(): boolean {
  return typeof window !== "undefined";
}

function canUseCordovaKeycloakBridge(): boolean {
  return canUseWindow() && window.parent != null && window.parent !== window;
}

function shouldPreferCordovaKeycloakBridge(): boolean {
  return isAppRuntime() || canUseCordovaKeycloakBridge();
}

function postCordovaKeycloakMessage(message: string): void {
  if (!canUseWindow()) {
    return;
  }

  if (typeof window.sendCordovaWrapperMessage === "function") {
    window.sendCordovaWrapperMessage(message);
    return;
  }

  if (window.parent && window.parent !== window) {
    window.parent.postMessage(message, "*");
  }
}

function requestLegacyLogin(): void {
  postCordovaKeycloakMessage("KC_INIT");
}

function requestLegacyLogout(redirectUri: string): void {
  postCordovaKeycloakMessage(`KC_LOGOUT:${redirectUri}`);
}

function buildRuntimeAssetUrl(assetPath: string): string | undefined {
  if (!canUseWindow()) {
    return undefined;
  }

  const currentUrl = new URL(window.location.href);
  const basePath = currentUrl.pathname.endsWith("/")
    ? currentUrl.pathname
    : currentUrl.pathname.replace(/[^/]*$/, "/");

  return new URL(
    assetPath.replace(/^\/+/, ""),
    `${currentUrl.origin}${basePath}`,
  ).toString();
}

function buildRuntimeRedirectUri(
  explicitRedirectUri?: string,
): string | undefined {
  if (explicitRedirectUri && explicitRedirectUri.trim().length > 0) {
    return explicitRedirectUri;
  }

  if (!canUseWindow()) {
    return explicitRedirectUri;
  }

  const baseUrl = `${window.location.origin}${window.location.pathname}${window.location.search}`;
  const initialHash = stripAuthResponseFragment(
    window.__wktInitialLocationHash || "",
  );
  const currentHash = stripAuthResponseFragment(window.location.hash || "");
  const preferredHash =
    currentHash && currentHash !== "#" && currentHash !== "#/"
      ? currentHash
      : initialHash;
  const normalizedHash = preferredHash.startsWith("#/")
    ? preferredHash
    : (normalizeLegacyHash(preferredHash) ?? preferredHash);

  if (!normalizedHash || normalizedHash === "#" || normalizedHash === "#/") {
    return baseUrl;
  }

  return `${baseUrl}${normalizedHash}`;
}

function resolveRuntimeResponseMode(
  explicitResponseMode?: "query" | "fragment",
): "query" | "fragment" | undefined {
  if (explicitResponseMode) {
    return explicitResponseMode;
  }

  if (!canUseWindow()) {
    return undefined;
  }

  return shouldPreferCordovaKeycloakBridge() ? "fragment" : "query";
}

function resolveSilentCheckSsoRedirectUri(
  explicitRedirectUri?: string,
): string | undefined {
  if (explicitRedirectUri && explicitRedirectUri.trim().length > 0) {
    return explicitRedirectUri;
  }

  return buildRuntimeAssetUrl("silent-check-sso.html");
}

export function getKeycloakInstance(): KeycloakInstanceLike | null {
  if (!canUseWindow() || shouldPreferCordovaKeycloakBridge()) {
    return null;
  }

  return (
    (window.keycloak as KeycloakInstanceLike | undefined) ??
    runtimeInstance ??
    null
  );
}

function ensureKeycloakInstance(): KeycloakInstanceLike | null {
  const existingInstance = getKeycloakInstance();
  if (existingInstance) {
    runtimeInstance = existingInstance;
    return existingInstance;
  }

  const configUrl = buildRuntimeAssetUrl("keycloak.json");
  if (!configUrl) {
    return null;
  }

  const instance = new Keycloak(configUrl);
  runtimeInstance = instance;
  window.keycloak = instance;
  return instance;
}

function hasRuntimeProfile(): boolean {
  const profile = getKeycloakProfile();
  return (
    typeof profile?.username === "string" ||
    typeof profile?.email === "string" ||
    typeof profile?.id === "string"
  );
}

function syncTokenFromKeycloak(
  instance?: KeycloakInstanceLike | null,
): string | null {
  const token = instance?.token ?? getAccessToken();
  setAccessToken(token ?? null);
  return token ?? null;
}

function isAuthenticatedRuntime(
  instance?: KeycloakInstanceLike | null,
): boolean {
  if (instance?.authenticated === true) {
    syncTokenFromKeycloak(instance);
    return true;
  }

  return syncTokenFromKeycloak(instance) != null;
}

export function getKeycloakProfile(): KeycloakProfile | null {
  if (!canUseWindow()) {
    return runtimeProfile;
  }

  return (window.kc_profile as KeycloakProfile | undefined) ?? runtimeProfile;
}

async function storeProfileFromRuntime(): Promise<KeycloakProfile | null> {
  const instance = ensureKeycloakInstance();
  if (!instance) {
    return getKeycloakProfile();
  }

  const profile = (await instance.loadUserProfile()) ?? null;
  const resolvedProfile = profile
    ? {
        ...profile,
        id: profile.id ?? instance.subject,
      }
    : null;

  runtimeProfile = resolvedProfile;
  if (canUseWindow()) {
    window.kc_profile = resolvedProfile;
  }

  return resolvedProfile;
}

async function waitForCordovaBridgeAuthentication(): Promise<boolean> {
  if (isAuthenticatedRuntime(null) || hasRuntimeProfile()) {
    return true;
  }

  if (!canUseWindow()) {
    return false;
  }

  for (let attempt = 0; attempt < CORDOVA_INIT_MAX_ATTEMPTS; attempt += 1) {
    if (attempt % 10 === 0) {
      requestLegacyLogin();
    }

    if (isAuthenticatedRuntime(null) || hasRuntimeProfile()) {
      return true;
    }

    await new Promise<void>((resolve) => {
      window.setTimeout(resolve, CORDOVA_INIT_WAIT_INTERVAL_MS);
    });
  }

  return isAuthenticatedRuntime(null) || hasRuntimeProfile();
}

export async function initKeycloak(
  options: KeycloakInitOptions = {},
): Promise<boolean> {
  if (shouldPreferCordovaKeycloakBridge()) {
    requestLegacyLogin();
    return waitForCordovaBridgeAuthentication();
  }

  const existingInstance = getKeycloakInstance();
  const instance = existingInstance ?? ensureKeycloakInstance();
  if (!instance) {
    return false;
  }

  if (isAuthenticatedRuntime(instance)) {
    if (!hasRuntimeProfile()) {
      try {
        await storeProfileFromRuntime();
      } catch (error) {
        console.warn(
          "Unable to load Keycloak profile from existing runtime",
          error,
        );
      }
    }

    return true;
  }

  if (instance.didInitialize) {
    syncTokenFromKeycloak(instance);
    return isAuthenticatedRuntime(instance);
  }

  if (initPromise) {
    return initPromise;
  }

  initPromise = (async () => {
    const responseMode = resolveRuntimeResponseMode(options.responseMode);
    const onLoad = options.onLoad ?? "check-sso";
    const initOptions: NativeKeycloakInitOptions = {
      onLoad,
      checkLoginIframe: options.checkLoginIframe ?? false,
      ...(responseMode ? { responseMode } : {}),
    };

    if (onLoad === "check-sso") {
      const silentCheckSsoRedirectUri = resolveSilentCheckSsoRedirectUri(
        options.silentCheckSsoRedirectUri,
      );

      if (silentCheckSsoRedirectUri) {
        initOptions.silentCheckSsoRedirectUri = silentCheckSsoRedirectUri;
        initOptions.silentCheckSsoFallback =
          options.silentCheckSsoFallback ?? false;
      }
    }

    const authenticated = await instance.init(initOptions);

    syncTokenFromKeycloak(instance);

    if (authenticated) {
      try {
        await storeProfileFromRuntime();
      } catch (error) {
        console.warn("Unable to load Keycloak profile after init", error);
      }
    }

    return authenticated === true;
  })();

  try {
    return await initPromise;
  } finally {
    initPromise = null;
  }
}

export async function login(options?: KeycloakLoginOptions): Promise<boolean> {
  if (shouldPreferCordovaKeycloakBridge()) {
    requestLegacyLogin();
    return waitForCordovaBridgeAuthentication();
  }

  const instance = ensureKeycloakInstance();
  if (!instance) {
    requestLegacyLogin();
    return false;
  }

  const redirectUri = buildRuntimeRedirectUri(options?.redirectUri);
  await instance.login({
    ...(options ?? {}),
    ...(redirectUri ? { redirectUri } : {}),
  });
  syncTokenFromKeycloak(instance);

  try {
    await storeProfileFromRuntime();
  } catch (error) {
    console.warn("Unable to load Keycloak profile after login", error);
  }

  return true;
}

export async function logout(redirectUri?: string): Promise<void> {
  if (shouldPreferCordovaKeycloakBridge()) {
    runtimeProfile = null;
    if (canUseWindow()) {
      window.kc_profile = undefined;
    }
    requestLegacyLogout(redirectUri ?? window.location.href);
    clearAuthContext({
      preserveClientCompany: false,
      preserveDeviceUuid: true,
      preserveKioskEmployee: false,
    });
    return;
  }

  const instance = ensureKeycloakInstance();
  if (!instance) {
    requestLegacyLogout(redirectUri ?? window.location.href);
    return;
  }

  await instance.logout({ redirectUri });

  runtimeProfile = null;
  if (canUseWindow()) {
    window.kc_profile = undefined;
  }

  clearAuthContext({
    preserveClientCompany: false,
    preserveDeviceUuid: true,
    preserveKioskEmployee: false,
  });
}

export async function refreshToken(minValidity = 10): Promise<boolean> {
  if (shouldPreferCordovaKeycloakBridge()) {
    return isAuthenticatedRuntime(null);
  }

  const instance = ensureKeycloakInstance();
  if (!instance || !instance.didInitialize) {
    return false;
  }

  const refreshed = await instance.updateToken(minValidity);
  syncTokenFromKeycloak(instance);

  if (!getKeycloakProfile()) {
    try {
      await storeProfileFromRuntime();
    } catch (error) {
      console.warn("Unable to refresh Keycloak profile", error);
    }
  }

  return refreshed === true;
}

export function isKeycloakAuthenticated(): boolean {
  const instance = getKeycloakInstance();
  if (instance?.authenticated === true) {
    syncTokenFromKeycloak(instance);
    return true;
  }

  return getAccessToken() != null;
}

export function scheduleTokenRefresh(
  options: { intervalMs?: number; minValidity?: number } = {},
): void {
  if (!canUseWindow()) {
    return;
  }

  stopTokenRefresh();

  const intervalMs = options.intervalMs ?? 10_000;
  const minValidity = options.minValidity ?? 10;
  refreshIntervalId = window.setInterval(() => {
    void refreshToken(minValidity).catch((error) => {
      console.warn("Automatic Keycloak refresh failed", error);
    });
  }, intervalMs);
}

export function stopTokenRefresh(): void {
  if (!canUseWindow() || refreshIntervalId == null) {
    return;
  }

  window.clearInterval(refreshIntervalId);
  refreshIntervalId = null;
}
